import { useState } from 'react';
import { useAsyncAction } from 'src/design/molecules/useAsyncAction';

export interface IConfirmableAction<TArgs extends unknown[] = []> {
  opened: boolean;
  loading: boolean;
  /** Icon onClick: opens the modal when confirmation is required, else runs immediately with these args. */
  requestOrRun: (...args: TArgs) => void;
  /** Modal action button: runs the action, closes on success, returns the boolean. */
  confirm: (...args: TArgs) => Promise<boolean>;
  close: () => void;
}

/** @registry Headless confirm-then-run state machine shared by confirmation action icons: gates a modal open/close around useAsyncAction so components only wire requestOrRun/confirm/close. */
export const useConfirmableAction = <TArgs extends unknown[] = []>(
  action: (...args: TArgs) => void | Promise<void>,
  requiredConfirmation: boolean,
): IConfirmableAction<TArgs> => {
  const [opened, setOpened] = useState(false);
  const asyncAction = useAsyncAction(action);

  const requestOrRun = (...args: TArgs) => {
    if (requiredConfirmation) {
      setOpened(true);
    } else {
      asyncAction.run(...args);
    }
  };

  const confirm = async (...args: TArgs): Promise<boolean> => {
    const success = await asyncAction.run(...args);
    if (success) setOpened(false);
    return success;
  };

  const close = () => setOpened(false);

  return { opened, loading: asyncAction.loading, requestOrRun, confirm, close };
};
